/**
 * ScheduleDrawer — edit drawer for a scheduled report.
 *
 * Form fields: description, frequency, day selector, send time,
 * attachments, applied filters summary, email recipients.
 *
 * Props:
 *   open, onClose, onSave, schedule (row data), frequencies,
 *   filterTermsSummary, onEditFilters, saving
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { convertSectoHHMM } from '../../../../libs.jsx';
import '../../alert-details/alert-details-page.css';

const WEEKLY_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHLY_DAYS = [...Array.from({ length: 31 }, (_, i) => i + 1), 0]; // 1-31 + 0=Last Day

function secondsToTimeInput(sec) {
    if (sec == null) return '09:00';
    const totalSec = parseInt(sec, 10);
    if (isNaN(totalSec)) return '09:00';
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function timeInputToSeconds(timeStr) {
    if (!timeStr) return 32400; // 9:00 AM
    const [h, m] = timeStr.split(':').map(Number);
    return (h * 3600) + (m * 60);
}

function parseFilterSummary(filterTerms) {
    if (!filterTerms) return [];
    try {
        const parsed = typeof filterTerms === 'string' ? JSON.parse(filterTerms) : filterTerms;
        const chips = [];
        const labelMap = {
            area_filter: 'Areas', area_group_filter: 'Area Groups', area_type_filter: 'Area Types',
            resident_filter: 'Residents', resident_group_filter: 'Resident Groups',
            user_filter: 'Users', user_group_filter: 'User Groups',
            device_category_filter: 'Device Categories', device_filter: 'Devices',
            event_type_filter: 'Event Types', reason_filter: 'Reasons',
        };
        Object.entries(labelMap).forEach(([key, label]) => {
            const val = parsed[key]?.values;
            if (val && val !== '') {
                const count = String(val).split(',').filter(Boolean).length;
                chips.push({ key, label: `${label}: ${count}` });
            }
        });
        if (parsed.resp_filter_items?.values?.[0]) {
            chips.push({ key: 'resp', label: parsed.resp_filter_items.labels || 'Response Time' });
        }
        return chips;
    } catch { return []; }
}

const ScheduleDrawer = ({ open, onClose, onSave, schedule, frequencies, onEditFilters, saving, filterOptions }) => {
    const [description, setDescription] = useState('');
    const [frequencyId, setFrequencyId] = useState(1);
    const [selectedDays, setSelectedDays] = useState(new Set());
    const [sendTime, setSendTime] = useState('09:00');
    const [pdfSummary, setPdfSummary] = useState(false);
    const [pdfDetails, setPdfDetails] = useState(false);
    const [csvDetails, setCsvDetails] = useState(false);
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmail] = useState('');
    const [emailError, setEmailError] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(new Set());
    const [userSearch, setUserSearch] = useState('');
    const emailInputRef = useRef(null);

    const isExecutive = schedule?.report_type_id === 5;
    const filterChips = parseFilterSummary(schedule?.report_filter_terms);

    // Users list from filterOptions
    const userOptions = useMemo(() => filterOptions?.users || [], [filterOptions]);
    const filteredUserOptions = useMemo(() => {
        if (!userSearch) return userOptions;
        const term = userSearch.toLowerCase();
        return userOptions.filter((u) =>
            (u.display_name || '').toLowerCase().includes(term) ||
            (u.email || '').toLowerCase().includes(term)
        );
    }, [userOptions, userSearch]);

    // Populate form when drawer opens
    useEffect(() => {
        if (open && schedule) {
            setDescription(schedule.description || '');
            setFrequencyId(schedule.issue_freq_id || 1);
            setSendTime(secondsToTimeInput(schedule.send_report_time));
            setPdfSummary(Boolean(schedule.pdf_summary));
            setPdfDetails(Boolean(schedule.pdf_details));
            setCsvDetails(Boolean(schedule.csv_details));

            // Parse frequency reference (day selections)
            const refStr = schedule.window_frequency_reference || '';
            const days = refStr.split(',').filter(Boolean).map((d) => d.trim());
            setSelectedDays(new Set(days));

            // Parse email recipients
            const rcv = schedule.report_receiver || '';
            setEmails(rcv.split(',').filter(Boolean).map((e) => e.trim()));
            setNewEmail('');
            setEmailError('');

            // Parse user recipients
            const userIds = schedule.report_user_ids || '';
            setSelectedUserIds(new Set(userIds.split(',').filter(Boolean).map((id) => id.trim())));
            setUserSearch('');
        }
    }, [open, schedule]);

    // Focus first input
    useEffect(() => {
        if (open) {
            const timer = setTimeout(() => {
                document.getElementById('sr-description')?.focus();
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [open]);

    // Escape key
    useEffect(() => {
        const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
        if (open) document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, [open, onClose]);

    const toggleDay = useCallback((day) => {
        setSelectedDays((prev) => {
            const next = new Set(prev);
            const val = String(day);
            if (next.has(val)) next.delete(val); else next.add(val);
            return next;
        });
    }, []);

    const handleToggleUser = useCallback((userId) => {
        setSelectedUserIds((prev) => {
            const next = new Set(prev);
            const id = String(userId);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    }, []);

    const handleSelectAllUsers = useCallback(() => {
        setSelectedUserIds(new Set(filteredUserOptions.filter((u) => Boolean(u.email)).map((u) => String(u.user_id))));
    }, [filteredUserOptions]);

    const handleClearUsers = useCallback(() => {
        setSelectedUserIds(new Set());
    }, []);

    const handleAddEmail = useCallback(() => {
        const email = newEmail.trim().toLowerCase();
        const pattern = /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/;
        if (!email) return;
        if (!pattern.test(email)) {
            setEmailError('Invalid email format');
            return;
        }
        if (emails.includes(email)) {
            setEmailError('Email already added');
            return;
        }
        setEmails((prev) => [...prev, email]);
        setNewEmail('');
        setEmailError('');
    }, [newEmail, emails]);

    const handleRemoveEmail = useCallback((email) => {
        setEmails((prev) => prev.filter((e) => e !== email));
    }, []);

    const handleSubmit = useCallback(() => {
        const freqRate = frequencyId === 1 ? ['1'] : [...selectedDays];

        onSave({
            report_id: schedule.report_id,
            description,
            issue_freq_id: frequencyId,
            window_frequency_reference: freqRate.join(','),
            send_report_time: timeInputToSeconds(sendTime),
            pdf_summary: pdfSummary ? 1 : 0,
            pdf_details: pdfDetails ? 1 : 0,
            csv_details: csvDetails ? 1 : 0,
            report_receiver: emails.join(','),
            report_user_ids: [...selectedUserIds].join(','),
            report_filter_terms: schedule.report_filter_terms,
        });
    }, [schedule, description, frequencyId, selectedDays, sendTime, pdfSummary, pdfDetails, csvDetails, emails, selectedUserIds, onSave]);

    if (!schedule) return null;

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer dp-drawer--lg${open ? ' is-open' : ''}`} role="dialog" aria-modal="true" aria-label="Edit Scheduled Report">
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">Edit Scheduled Report</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'\u2715'}</button>
                </div>

                <div className="dp-drawer__body" style={{ overflowY: 'auto', flex: 1 }}>

                    {/* ── Schedule Details ── */}
                    <div className="sr-form-card">
                        <h3 className="sr-form-card__title">Schedule Details</h3>

                        {/* Report Type (read-only) */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Report Type</span>
                            <span style={{ fontSize: 'var(--font-size-sm)', textTransform: 'capitalize', color: 'var(--color-text-secondary)' }}>
                                {schedule.report_type_name || '--'}
                            </span>
                        </div>

                        {/* Description */}
                        <div className="sr-form-section">
                            <label htmlFor="sr-description" className="sr-form-section__label">Description</label>
                            <input
                                id="sr-description"
                                type="text"
                                className="sr-email-input"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                                pattern="^[0-9a-zA-Z_@\- ]+$"
                                required
                            />
                        </div>

                        {/* Frequency */}
                        <div className="sr-form-section">
                            <label htmlFor="sr-frequency" className="sr-form-section__label">Delivery Rate</label>
                            <select
                                id="sr-frequency"
                                className="sr-time-input"
                                value={frequencyId}
                                onChange={(e) => { setFrequencyId(parseInt(e.target.value, 10)); setSelectedDays(new Set()); }}
                                style={{ width: '100%' }}
                            >
                                {(frequencies || []).map((f) => (
                                    <option key={f.window_frequency_id} value={f.window_frequency_id}>{f.frequency_name}</option>
                                ))}
                            </select>
                        </div>

                        {/* Day selector — Weekly */}
                        {frequencyId === 2 ? (
                            <div className="sr-form-section">
                                <span className="sr-form-section__label">Days of Week</span>
                                <div className="sr-day-picker">
                                    {WEEKLY_DAYS.map((day, idx) => (
                                        <button
                                            key={idx}
                                            type="button"
                                            className={`sr-day-chip${selectedDays.has(String(idx)) ? ' is-selected' : ''}`}
                                            onClick={() => toggleDay(idx)}
                                        >
                                            {day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {/* Day selector — Monthly */}
                        {frequencyId === 3 ? (
                            <div className="sr-form-section">
                                <span className="sr-form-section__label">Days of Month</span>
                                <div className="sr-day-picker">
                                    {MONTHLY_DAYS.map((day) => (
                                        <button
                                            key={day}
                                            type="button"
                                            className={`sr-day-chip${selectedDays.has(String(day)) ? ' is-selected' : ''}`}
                                            onClick={() => toggleDay(day)}
                                        >
                                            {day === 0 ? 'Last' : day}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        ) : null}

                        {/* Send Time */}
                        <div className="sr-form-section">
                            <label htmlFor="sr-sendtime" className="sr-form-section__label">Time of Day</label>
                            <input
                                id="sr-sendtime"
                                type="time"
                                className="sr-time-input"
                                value={sendTime}
                                onChange={(e) => setSendTime(e.target.value)}
                                step={300}
                            />
                        </div>
                    </div>

                    {/* ── Attachments & Filters ── */}
                    <div className="sr-form-card">
                        <h3 className="sr-form-card__title">Attachments &amp; Filters</h3>

                        {/* Attachments */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Attachments</span>
                            <div className="sr-attachments">
                                <label className="sr-attachment-item">
                                    <input type="checkbox" checked={pdfSummary} onChange={(e) => setPdfSummary(e.target.checked)} />
                                    PDF Attachment (Summary)
                                </label>
                                <label className="sr-attachment-item">
                                    <input type="checkbox" checked={pdfDetails} onChange={(e) => setPdfDetails(e.target.checked)} />
                                    PDF Attachment (Details)
                                </label>
                                {!isExecutive ? (
                                    <label className="sr-attachment-item">
                                        <input type="checkbox" checked={csvDetails} onChange={(e) => setCsvDetails(e.target.checked)} />
                                        CSV Attachment (Details)
                                    </label>
                                ) : null}
                            </div>
                        </div>

                        {/* Applied Filters */}
                        <div className="sr-form-section">
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-2)' }}>
                                <span className="sr-form-section__label" style={{ marginBottom: 0 }}>Applied Filters</span>
                                <button type="button" className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 10px' }} onClick={onEditFilters}>
                                    <i className="fe fe-sliders" style={{ marginRight: '4px' }} />Edit Filters
                                </button>
                            </div>
                            <div className="sr-filters-summary">
                                {filterChips.length === 0 ? (
                                    <span className="sr-no-filters">No filters applied</span>
                                ) : null}
                                {filterChips.map((chip) => (
                                    <span key={chip.key} className="sr-filter-tag">
                                        <span className="sr-filter-tag__label">{chip.label}</span>
                                    </span>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* ── Recipients ── */}
                    <div className="sr-form-card">
                        <h3 className="sr-form-card__title">Recipients</h3>

                        {/* User Recipients */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">User Recipients</span>
                            <span className="sr-form-section__sublabel">
                                Select users whose email addresses will receive this report
                                {selectedUserIds.size > 0 ? ` (${selectedUserIds.size} selected)` : ''}
                            </span>
                            <div className="ar-filter-section" style={{ marginTop: '8px' }}>
                                <div className="ar-filter-section__header">
                                    <span className="ar-filter-section__label">Users</span>
                                    <div className="ar-filter-section__actions">
                                        <button className="ar-filter-section__btn" onClick={handleSelectAllUsers} type="button">Select All</button>
                                        <button className="ar-filter-section__btn" onClick={handleClearUsers} type="button">Clear</button>
                                    </div>
                                </div>
                                <input
                                    className="ar-filter-section__search"
                                    type="text"
                                    placeholder="Search users..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                />
                                <div className="ar-filter-list">
                                    {filteredUserOptions.length === 0 ? (
                                        <p className="ar-filter-list__empty">No users found.</p>
                                    ) : null}
                                    {filteredUserOptions.map((user) => {
                                        const id = String(user.user_id);
                                        const checked = selectedUserIds.has(id);
                                        const hasEmail = Boolean(user.email);
                                        return (
                                            <label key={id} className={`ar-filter-list__item ar-filter-list__item--has-subtext${!hasEmail ? ' ar-filter-list__item--disabled' : ''}`}>
                                                <input
                                                    type="checkbox"
                                                    checked={checked}
                                                    onChange={() => handleToggleUser(id)}
                                                    disabled={!hasEmail}
                                                />
                                                <span className="ar-filter-list__item-content">
                                                    <span className="ar-filter-list__item-name">{user.display_name || '(unknown)'}</span>
                                                    <span className="ar-filter-list__item-subtext">
                                                        {hasEmail ? user.email : 'No email on file'}
                                                    </span>
                                                </span>
                                            </label>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>

                        {/* Email Recipients */}
                        <div className="sr-form-section">
                            <span className="sr-form-section__label">Email Recipients</span>
                            <div className="sr-email-list">
                                {emails.map((email) => (
                                    <span key={email} className="sr-email-chip">
                                        {email}
                                        <button type="button" className="sr-email-chip__remove" onClick={() => handleRemoveEmail(email)} aria-label={`Remove ${email}`}>
                                            {'\u2715'}
                                        </button>
                                    </span>
                                ))}
                            </div>
                            <div className="sr-email-add-row">
                                <input
                                    ref={emailInputRef}
                                    type="email"
                                    className="sr-email-input"
                                    placeholder="name@example.com"
                                    value={newEmail}
                                    onChange={(e) => { setNewEmail(e.target.value); setEmailError(''); }}
                                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(); } }}
                                />
                                <button type="button" className="rp-btn rp-btn--secondary" onClick={handleAddEmail}>Add</button>
                            </div>
                            {emailError ? (
                                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-danger)', marginTop: '4px', display: 'block' }}>{emailError}</span>
                            ) : null}
                        </div>
                    </div>
                </div>

                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving} type="button">Cancel</button>
                    <button className="dp-btn dp-btn--primary" onClick={handleSubmit} disabled={saving || !description.trim()} type="button">
                        {saving ? 'Saving\u2026' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </>
    );
};

export default ScheduleDrawer;
